// Pure helpers for "where is this used" and for moving things between groups, locations and
// broadcasts. Nothing here touches Firebase: the callers write the result with `updateMany`
// (lib/firebase.js) as ONE atomic update.
//
// How things are connected:
// - a group and a location each have ONE `broadcastId` (a location's own broadcast wins over
//   the one of its group)
// - a location has ONE `groupId` (required)
// - a composite broadcast lists broadcasts in `broadcastList`; it may list a multiplier copy
//   (`sourceId` = the original) instead of the original itself

import { compareValues } from "lib/sort"
import { isCompositeBroadcast } from "lib/playlist"

const byName = (a, b) => compareValues(a.name, b.name)

const pushTo = (map, key, value) => {
  if (!map.has(key)) map.set(key, [])
  map.get(key).push(value)
}

// Which names a narrow table cell shows. A table row has room for `lines` (three) lines of text and
// every name gets a line of its own: up to three names are all shown; with more, the first two are
// shown and the last line says "+N more". Returns { shown, more }.
export const summarizeNames = (names, lines = 3) => {
  const list = (names || []).filter(Boolean)
  if (list.length <= lines) return { shown: list, more: 0 }
  const shown = list.slice(0, lines - 1)
  return { shown, more: list.length - shown.length }
}

// Where every broadcast is used. `broadcasts` has to contain the multiplier copies too.
//  - groups:     Map(broadcastId -> groups that play it)
//  - locations:  Map(broadcastId -> locations that have it as their OWN broadcast)
//  - composites: Map(regularBroadcastId -> [{ id, name, count }]) the composite broadcasts that
//                list it (or one of its multiplier copies); `count` = how many times
// Every list is sorted by name.
export const broadcastLinks = ({ broadcasts, groups, locations }) => {
  const byId = new Map((broadcasts || []).map(b => [b.id, b]))

  const groupMap = new Map()
  for (const group of groups || []) {
    if (group.broadcastId) pushTo(groupMap, group.broadcastId, group)
  }

  const locationMap = new Map()
  for (const location of locations || []) {
    if (location.broadcastId) pushTo(locationMap, location.broadcastId, location)
  }

  const compositeMap = new Map()
  for (const composite of broadcasts || []) {
    if (!isCompositeBroadcast(composite)) continue
    const counts = new Map()
    for (const id of composite.broadcastList || []) {
      const entry = byId.get(id)
      if (!entry) continue
      const origin = entry.sourceId || entry.id
      counts.set(origin, (counts.get(origin) || 0) + 1)
    }
    for (const [origin, count] of counts) {
      pushTo(compositeMap, origin, { id: composite.id, name: composite.name, count })
    }
  }

  for (const map of [groupMap, locationMap, compositeMap]) {
    map.forEach(list => list.sort(byName))
  }
  return { groups: groupMap, locations: locationMap, composites: compositeMap }
}

// Map(groupId -> locations of that group), each list sorted by name
export const locationsByGroup = (locations) => {
  const map = new Map()
  for (const location of locations || []) {
    if (location.groupId) pushTo(map, location.groupId, location)
  }
  map.forEach(list => list.sort(byName))
  return map
}

// Sets (`add`) or clears (`remove`) the broadcast of several groups or locations.
//  - node:    "group" or "location"
//  - records: all records of that node, each with its `id`
//  - add / remove: ids. A record that already has the broadcast is not touched when adding; when
//    removing, only a record that STILL has this broadcast is cleared (one that was given
//    another broadcast meanwhile is left alone).
// Returns { updates, added, removed, replaced }: `replaced` = the ids from `added` that had
// ANOTHER broadcast, which this one takes the place of.
export const planAssignBroadcast = ({ node, records, broadcastId, add = [], remove = [], now = new Date().toISOString() }) => {
  const byId = new Map(records.map(r => [r.id, r]))
  const updates = {}
  const added = []
  const removed = []
  const replaced = []

  // A group must always have a broadcast (the device reads it from the group when the location
  // has none), so a group can only be given another broadcast, never left without one
  const toRemove = node === "group" ? [] : remove

  for (const id of new Set(toRemove)) {
    const record = byId.get(id)
    if (record && record.broadcastId === broadcastId) {
      updates[`${node}/${id}/broadcastId`] = null
      updates[`${node}/${id}/updated_at`] = now
      removed.push(id)
    }
  }
  for (const id of new Set(add)) {
    const record = byId.get(id)
    if (record && record.broadcastId !== broadcastId) {
      if (record.broadcastId) replaced.push(id)
      updates[`${node}/${id}/broadcastId`] = broadcastId
      updates[`${node}/${id}/updated_at`] = now
      added.push(id)
    }
  }
  return { updates, added, removed, replaced }
}

// Moves locations into a group. A location has exactly one group, so there is nothing to
// "remove": the group of each location in `add` is simply replaced. Locations that are in the
// group already are not touched.
// Returns { updates, moved }: `moved` = the ids that really changed group.
export const planGroupLocations = ({ locations, groupId, add = [], now = new Date().toISOString() }) => {
  const byId = new Map(locations.map(l => [l.id, l]))
  const updates = {}
  const moved = []
  for (const id of new Set(add)) {
    const record = byId.get(id)
    if (record && record.groupId !== groupId) {
      updates[`location/${id}/groupId`] = groupId
      updates[`location/${id}/updated_at`] = now
      moved.push(id)
    }
  }
  return { updates, moved }
}

// The starting values of the "Copy group" form: everything of the group except what identifies it
// (`id`, the denormalised `broadcast` object, `updated_at`), with an EMPTY name for the new group.
// The `broadcast` stub is what the group form expects when "new broadcast" is chosen there.
export const groupTemplate = (group) => {
  const { id, broadcast, updated_at, clientId, ...rest } = group
  return { ...rest, name: "", broadcast: { name: "", type: "regular" } }
}
// The starting values of the "Copy location" form: everything of the location except what
// identifies it or belongs to the place it is (`id`, the last ping / sync its device reported,
// `updated_at`, the coordinates: they are found again from the address when the copy is saved),
// and without the objects the app only adds for showing it (`group`, `broadcast`). The name is empty.
// The device stays with the original: a location has one device, and it is chosen in the device form.
export const locationTemplate = (location) => {
  const { id, group, broadcast, updated_at, clientId, lastPing, lastSync, latitude, longitude, ...rest } = location
  return { ...rest, name: "" }
}